import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-scroll";
import HomeIcon from "./Icons/HomeIcon";
import PersonIcon from "./Icons/PersonIcon";
import WorkIcon from "./Icons/WorkIcon";
import CodeIcon from "./Icons/CodeIcon";
import SchoolIcon from "./Icons/SchoolIcon";
import MailIcon from "./Icons/MailIcon";
import BarsIcon from "./Icons/BarsIcon";
import TimesIcon from "./Icons/TimesIcon";

const NavBar = () => {
  const [nav, setNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        nav &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setNav(false);
      }
    };

    if (nav) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [nav]);

  const links = [
    {
      id: 1,
      link: "home",
      icon: <HomeIcon size={18} />,
    },
    {
      id: 2,
      link: "about",
      icon: <PersonIcon size={18} />,
    },
    {
      id: 3,
      link: "project",
      icon: <WorkIcon size={18} />,
    },
    {
      id: 4,
      link: "skills",
      icon: <CodeIcon size={18} />,
    },
    {
      id: 5,
      link: "education",
      icon: <SchoolIcon size={18} />,
    },
    {
      id: 6,
      link: "contact",
      icon: <MailIcon size={18} />,
    },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 overflow-x-hidden w-full max-w-full ${
        scrolled
          ? "bg-gray-950 shadow-2xl shadow-black/30 border-b border-gray-800/50 md:bg-gray-950/95 md:backdrop-blur-xl"
          : "bg-gray-950 border-b border-gray-800/50 md:bg-gray-950/80 md:backdrop-blur-xl md:border-gray-800/30"
      }`}
      style={{ position: 'fixed', width: '100%', maxWidth: '100vw', left: 0, right: 0 }}
    >
      {/* Glassmorphism overlay - only on desktop */}
      <div className="hidden md:block absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>
      
      <div className="relative flex justify-between items-center w-full h-12 md:h-14 px-3 sm:px-4 md:px-5 lg:px-6 max-w-7xl mx-auto overflow-x-hidden" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Logo - Modern Design */}
        <Link
          to="home"
          smooth
          duration={500}
          className="cursor-pointer group flex items-center relative z-10"
        >
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/50 via-cyan-500/50 to-purple-500/50 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Logo container */}
            <div className="relative w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-blue-600 via-cyan-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:shadow-xl group-hover:shadow-blue-500/40 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 border border-white/10">
              <span className="text-white font-extrabold text-sm md:text-base tracking-tight drop-shadow-lg">YB</span>
            </div>
          </div>
        </Link>

        {/* Desktop Navigation - Modern Pill Design */}
        <ul className="hidden md:flex items-center gap-1.5 bg-gray-900/50 backdrop-blur-xl px-2 py-1.5 rounded-full border border-gray-800/60 shadow-xl shadow-black/30 ring-1 ring-white/5">
          {links.map(({ id, link, icon }) => {
            // Color mapping for each icon
            const iconColors = {
              1: { gradient: "linear-gradient(to bottom right, rgb(59 130 246), rgb(6 182 212))", text: "text-blue-400", border: "border-blue-500/50", shadow: "shadow-blue-500/30", bg: "linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(6, 182, 212, 0.1))", dot: "linear-gradient(to right, rgb(59 130 246), rgb(6 182 212))" },
              2: { gradient: "linear-gradient(to bottom right, rgb(168 85 247), rgb(236 72 153))", text: "text-purple-400", border: "border-purple-500/50", shadow: "shadow-purple-500/30", bg: "linear-gradient(to right, rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.1))", dot: "linear-gradient(to right, rgb(168 85 247), rgb(236 72 153))" },
              3: { gradient: "linear-gradient(to bottom right, rgb(249 115 22), rgb(239 68 68))", text: "text-orange-400", border: "border-orange-500/50", shadow: "shadow-orange-500/30", bg: "linear-gradient(to right, rgba(249, 115, 22, 0.1), rgba(239, 68, 68, 0.1))", dot: "linear-gradient(to right, rgb(249 115 22), rgb(239 68 68))" },
              4: { gradient: "linear-gradient(to bottom right, rgb(34 197 94), rgb(16 185 129))", text: "text-green-400", border: "border-green-500/50", shadow: "shadow-green-500/30", bg: "linear-gradient(to right, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.1))", dot: "linear-gradient(to right, rgb(34 197 94), rgb(16 185 129))" },
              5: { gradient: "linear-gradient(to bottom right, rgb(234 179 8), rgb(245 158 11))", text: "text-yellow-400", border: "border-yellow-500/50", shadow: "shadow-yellow-500/30", bg: "linear-gradient(to right, rgba(234, 179, 8, 0.1), rgba(245, 158, 11, 0.1))", dot: "linear-gradient(to right, rgb(234 179 8), rgb(245 158 11))" },
              6: { gradient: "linear-gradient(to bottom right, rgb(99 102 241), rgb(59 130 246))", text: "text-indigo-400", border: "border-indigo-500/50", shadow: "shadow-indigo-500/30", bg: "linear-gradient(to right, rgba(99, 102, 241, 0.1), rgba(59, 130, 246, 0.1))", dot: "linear-gradient(to right, rgb(99 102 241), rgb(59 130 246))" },
            };
            const colors = iconColors[id] || iconColors[1];
            
            return (
              <li key={id} className="relative">
                <Link
                  to={link}
                  smooth
                  duration={500}
                  className="relative px-3 py-1.5 cursor-pointer capitalize text-xs font-medium text-gray-300 hover:text-white transition-all duration-300 group flex items-center gap-2 rounded-full"
                  activeClass="text-white"
                  spy={true}
                  offset={-56}
                >
                  {/* Background gradient */}
                  <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: colors.bg }}></span>
                  
                  {/* Icon container with modern colors */}
                  <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-lg bg-gray-800/40 border border-gray-700/30 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:${colors.border}`} style={{ background: 'transparent', borderColor: 'transparent' }}>
                    <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: colors.gradient }}></div>
                    <span className={`relative z-10 group-hover:scale-110 transition-transform duration-300 ${colors.text} group-hover:text-white`}>
                      {icon}
                    </span>
                  </div>
                  
                  {/* Text */}
                  <span className="relative z-10 hidden lg:inline-block font-medium">
                    {link === "skills" ? "tech/skills" : link}
                  </span>
                  
                  {/* Active indicator dot */}
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg" style={{ background: colors.dot }}></span>
            </Link>
          </li>
            );
          })}
      </ul>

        {/* Mobile Menu Button - Modern Design */}
        <button
          ref={buttonRef}
          onClick={() => setNav(!nav)}
          className="cursor-pointer z-50 md:hidden relative w-10 h-10 flex items-center justify-center rounded-lg bg-gray-900/40 backdrop-blur-sm border border-gray-800/50 hover:bg-gray-800/60 transition-all duration-300 group"
          aria-label="Toggle menu"
        >
          <div className="relative w-6 h-6 flex items-center justify-center">
            <BarsIcon
              className={`absolute transition-all duration-300 text-gray-300 group-hover:text-white ${
                nav ? "opacity-0 rotate-90 scale-0" : "opacity-100 rotate-0 scale-100"
              }`}
              size={20}
            />
            <TimesIcon
              className={`absolute transition-all duration-300 text-gray-300 group-hover:text-white ${
                nav ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0"
              }`}
              size={20}
            />
          </div>
        </button>

        {/* Mobile Menu - Modern Slide-in */}
        {nav && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setNav(false)}
          ></div>
        )}
        <div
          ref={menuRef}
          className={`fixed top-0 right-0 w-64 max-w-[75vw] h-screen bg-gray-950/98 backdrop-blur-2xl border-l border-gray-800/50 shadow-2xl transition-all duration-500 ease-out md:hidden overflow-y-auto z-50 ${
            nav
              ? "opacity-100 visible translate-x-0"
              : "opacity-0 invisible translate-x-full"
          }`}
        >
          {/* Close button */}
          <div className="flex justify-end p-2 sm:p-3">
            <button
              onClick={() => setNav(false)}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-gray-900/40 backdrop-blur-sm border border-gray-800/50 hover:bg-gray-800/60 transition-all duration-300"
              aria-label="Close menu"
            >
              <TimesIcon className="text-gray-300" size={18} />
            </button>
          </div>

           {/* Navigation Links */}
           <div className="px-3 sm:px-4 mt-4 sm:mt-6">
             <ul className="flex flex-col gap-1.5 sm:gap-2 bg-gray-900/30 backdrop-blur-sm rounded-xl border border-gray-800/40 p-2 sm:p-2.5 shadow-lg shadow-black/20">
               {links.map(({ id, link, icon }, index) => {
                 // Color mapping for each icon
                 const iconColors = {
                   1: { gradientFrom: "from-blue-500", gradientTo: "to-cyan-500", text: "text-blue-400", border: "border-blue-500/50", shadow: "shadow-blue-500/30", bgFrom: "from-blue-600/20", bgTo: "to-cyan-600/20" },
                   2: { gradientFrom: "from-purple-500", gradientTo: "to-pink-500", text: "text-purple-400", border: "border-purple-500/50", shadow: "shadow-purple-500/30", bgFrom: "from-purple-600/20", bgTo: "to-pink-600/20" },
                   3: { gradientFrom: "from-orange-500", gradientTo: "to-red-500", text: "text-orange-400", border: "border-orange-500/50", shadow: "shadow-orange-500/30", bgFrom: "from-orange-600/20", bgTo: "to-red-600/20" },
                   4: { gradientFrom: "from-green-500", gradientTo: "to-emerald-500", text: "text-green-400", border: "border-green-500/50", shadow: "shadow-green-500/30", bgFrom: "from-green-600/20", bgTo: "to-emerald-600/20" },
                   5: { gradientFrom: "from-yellow-500", gradientTo: "to-amber-500", text: "text-yellow-400", border: "border-yellow-500/50", shadow: "shadow-yellow-500/30", bgFrom: "from-yellow-600/20", bgTo: "to-amber-600/20" },
                   6: { gradientFrom: "from-indigo-500", gradientTo: "to-blue-500", text: "text-indigo-400", border: "border-indigo-500/50", shadow: "shadow-indigo-500/30", bgFrom: "from-indigo-600/20", bgTo: "to-blue-600/20" },
                 };
                 const colors = iconColors[id] || iconColors[1];
                 
                 return (
                   <li
                     key={id}
                     className={`transition-all duration-300 ${
                       nav
                         ? "opacity-100 translate-x-0"
                         : "opacity-0 translate-x-8"
                     }`}
                     style={{
                       transitionDelay: nav ? `${index * 0.08}s` : "0s",
                     }}
            >
                     <Link
                       onClick={() => setNav(false)}
                       to={link}
                       smooth
                       duration={500}
                       className="relative flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 cursor-pointer capitalize text-sm sm:text-base font-medium text-gray-300 hover:text-white transition-all duration-300 rounded-lg group border border-transparent"
                       style={{ background: 'transparent' }}
                       onMouseEnter={(e) => {
                         e.currentTarget.style.background = colors.bg;
                         e.currentTarget.style.borderColor = colors.border;
                       }}
                       onMouseLeave={(e) => {
                         e.currentTarget.style.background = 'transparent';
                         e.currentTarget.style.borderColor = 'transparent';
                       }}
                     >
                       {/* Icon container with modern colors */}
                       <div className="relative z-10 flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-800/40 border border-gray-700/40 transition-all duration-300 group-hover:scale-110" style={{ background: 'transparent', borderColor: 'transparent' }}>
                         <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: colors.gradient }}></div>
                         <span className={`relative z-10 text-base sm:text-lg ${colors.text} group-hover:text-white transition-all duration-300`}>
                           {icon}
                         </span>
                       </div>
                       
                       {/* Link text */}
                       <span className="relative z-10 flex-1 text-sm sm:text-base font-medium">
                         {link === "skills" ? "tech/skills" : link}
                       </span>
                       
                       {/* Arrow indicator */}
                       <span className={`relative z-10 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1 ${colors.text}`}>
                         <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                         </svg>
                       </span>
              </Link>
            </li>
                 );
               })}
        </ul>
           </div>
        </div>
    </div>
    </nav>
  );
};

export default NavBar;
